import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Home,
  LogOut,
  FileText,
  Users,
  DollarSign,
  TrendingUp,
  Settings,
  Menu,
  X,
  Briefcase,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Edit2,
  Trash2,
  User,
  Clock,
  Target,
  ChevronRight,
  Building2,
  Gavel,
  Award,
  ExternalLink,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Zap,
  Send
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Realtor Sidebar
function RealtorSidebar({ realtor, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  
  const navItems = [
    { icon: Home, label: "Dashboard", path: "/realtor/dashboard" },
    { icon: FileText, label: "Listings", path: "/realtor/listings" },
    { icon: Briefcase, label: "Transactions", path: "/realtor/transactions" },
    { icon: Users, label: "Buyers", path: "/realtor/buyers" },
    { icon: Building2, label: "Sellers", path: "/realtor/sellers" },
    { icon: DollarSign, label: "Commission", path: "/realtor/commission" },
    { icon: TrendingUp, label: "Analytics", path: "/realtor/analytics" },
    { icon: Settings, label: "Settings", path: "/realtor/settings" },
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-black border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/realtor/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Home className="w-4 h-4 text-gold" />
            </div>
            <span className="font-heading font-bold text-white">REALTOR</span>
          </Link>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="text-white/50 hover:text-white p-1">
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      <nav className="p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = window.location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/50 hover:text-white hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full flex items-center justify-center">
              <span className="text-gold font-bold">{realtor?.name?.charAt(0) || "R"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{realtor?.name}</p>
              <p className="text-white/40 text-xs truncate">{realtor?.brokerage || "Agent"}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.08] hover:border-red-500/50 transition-all duration-300"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
}

// Motivation Badge
function MotivationBadge({ level }) {
  const colors = {
    very_motivated: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30",
    motivated: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30",
    normal: "bg-white/20/20 text-white/50 border-white/[0.15]/30",
    testing_market: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30"
  };
  const labels = {
    very_motivated: "Very Motivated",
    motivated: "Motivated",
    normal: "Normal",
    testing_market: "Testing Market"
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium border ${colors[level] || colors.normal}`}>
      {labels[level] || level}
    </span>
  );
}

// Status Badge
function StatusBadge({ status }) {
  const colors = {
    active: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30",
    listed: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30",
    under_contract: "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500/30",
    closed: "bg-white/20/20 text-white/50 border-white/[0.15]/30",
    lost: "bg-red-500/20 text-[#FF3B30] border-red-500/30"
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium border ${colors[status] || colors.active}`}>
      {status?.replace(/_/g, " ").toUpperCase()}
    </span>
  );
}

// Seller Card
function SellerCard({ seller, onEdit, onDelete, onConvert }) {
  const isFromMarketplace = seller.source === "marketplace";
  
  return (
    <div className={`bg-[#0A0A0A]/80 backdrop-blur-sm border ${isFromMarketplace ? "border-gold/30" : "border-white/[0.08]"} hover:border-gold/50 transition-all duration-300`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isFromMarketplace ? "bg-gold/20 border border-gold/50" : "bg-black border border-white/[0.08]"
            }`}>
              {isFromMarketplace ? (
                <Zap className="w-5 h-5 text-gold" />
              ) : (
                <User className="w-5 h-5 text-white/50" />
              )}
            </div>
            <div>
              <h3 className="text-white font-medium">{seller.name || "Unknown Seller"}</h3>
              {isFromMarketplace && (
                <span className="text-gold text-xs">From DealForge Marketplace</span>
              )}
            </div>
          </div>
          <StatusBadge status={seller.status} />
        </div>

        {/* Property Info */}
        <div className="bg-black border border-white/[0.08] p-3 mb-3">
          <p className="text-white font-medium mb-1">{seller.property_address}</p>
          <p className="text-white/50 text-sm">
            {seller.property_type} • {seller.bedrooms}bd/{seller.bathrooms}ba • {seller.sqft?.toLocaleString()} sqft
          </p>
          <p className="text-gold font-mono font-bold text-lg mt-2">{formatCurrency(seller.asking_price)}</p>
        </div>

        {/* Motivation & Timeline */}
        <div className="flex items-center gap-2 mb-3">
          <MotivationBadge level={seller.motivation_level} />
          <span className="text-white/50 text-xs flex items-center gap-1">
            <Clock className="w-3 h-3" /> {seller.timeline?.replace(/_/g, " ")}
          </span>
        </div>

        {/* Deal Analysis (for marketplace deals) */}
        {isFromMarketplace && seller.deal_analysis && (
          <div className="bg-gold/5 border border-gold/20 p-3 mb-3">
            <p className="text-gold text-xs font-medium mb-2">Deal Analysis</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {seller.deal_analysis.arv && (
                <div>
                  <span className="text-white/40">ARV:</span>
                  <span className="text-white ml-1">{formatCurrency(seller.deal_analysis.arv)}</span>
                </div>
              )}
              {seller.deal_analysis.rehab_estimate && (
                <div>
                  <span className="text-white/40">Rehab:</span>
                  <span className="text-white ml-1">{formatCurrency(seller.deal_analysis.rehab_estimate)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="flex items-center gap-4 text-sm text-white/50 mb-3">
          {seller.phone && (
            <a href={`tel:${seller.phone}`} className="flex items-center gap-1 hover:text-gold">
              <Phone className="w-3 h-3" /> {seller.phone}
            </a>
          )}
          {seller.email && (
            <a href={`mailto:${seller.email}`} className="flex items-center gap-1 hover:text-gold">
              <Mail className="w-3 h-3" /> {seller.email}
            </a>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-white/[0.08]">
          {seller.status === "active" && !seller.listing_id && (
            <Button
              size="sm"
              onClick={() => onConvert(seller.seller_id)}
              className="bg-gold hover:bg-gold/90 text-black rounded-none flex-1"
            >
              <ArrowRight className="w-4 h-4 mr-1" /> Convert to Listing
            </Button>
          )}
          {seller.listing_id && (
            <Link to={`/realtor/listings/${seller.listing_id}`} className="flex-1">
              <Button size="sm" variant="outline" className="border-gold text-gold hover:bg-gold/10 rounded-none w-full">
                <ExternalLink className="w-4 h-4 mr-1" /> View Listing
              </Button>
            </Link>
          )}
          <Button size="sm" variant="ghost" onClick={() => onEdit(seller)} className="text-white/50 hover:text-white">
            <Edit2 className="w-4 h-4" />
          </Button>
          {!isFromMarketplace && (
            <Button size="sm" variant="ghost" onClick={() => onDelete(seller.seller_id)} className="text-white/50 hover:text-[#FF3B30]">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Add/Edit Seller Modal
function SellerFormModal({ isOpen, onClose, seller, onSave }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    property_address: "",
    property_type: "SFH",
    bedrooms: 3,
    bathrooms: 2,
    sqft: 0,
    asking_price: 0,
    motivation_level: "normal",
    timeline: "flexible",
    notes: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (seller) {
      setForm({
        name: seller.name || "",
        email: seller.email || "",
        phone: seller.phone || "",
        property_address: seller.property_address || "",
        property_type: seller.property_type || "SFH",
        bedrooms: seller.bedrooms || 3,
        bathrooms: seller.bathrooms || 2,
        sqft: seller.sqft || 0,
        asking_price: seller.asking_price || 0,
        motivation_level: seller.motivation_level || "normal",
        timeline: seller.timeline || "flexible",
        notes: seller.notes || ""
      });
    } else {
      setForm({
        name: "",
        email: "",
        phone: "",
        property_address: "",
        property_type: "SFH",
        bedrooms: 3,
        bathrooms: 2,
        sqft: 0,
        asking_price: 0,
        motivation_level: "normal",
        timeline: "flexible",
        notes: ""
      });
    }
  }, [seller, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form, seller?.seller_id);
      onClose();
    } catch (err) {
      toast.error("Failed to save seller");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-lg my-8">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">
            {seller ? "Edit Seller" : "Add Seller"}
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Seller Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-white/50">Seller Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="bg-black border-white/[0.08] text-white mt-1"
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label className="text-white/50">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/50">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
          </div>

          {/* Property Info */}
          <div className="bg-black border border-white/[0.08] p-4">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gold" /> Property Details
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-white/50">Property Address *</Label>
                <Input
                  value={form.property_address}
                  onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                  required
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                  placeholder="123 Main St, Austin, TX 78701"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/50">Property Type</Label>
                  <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                    <SelectTrigger className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SFH">Single Family</SelectItem>
                      <SelectItem value="Condo">Condo</SelectItem>
                      <SelectItem value="Townhouse">Townhouse</SelectItem>
                      <SelectItem value="Multi-Family">Multi-Family</SelectItem>
                      <SelectItem value="Land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/50">Asking Price ($)</Label>
                  <Input
                    type="number"
                    value={form.asking_price}
                    onChange={(e) => setForm({ ...form, asking_price: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-white/50">Beds</Label>
                  <Input
                    type="number"
                    value={form.bedrooms}
                    onChange={(e) => setForm({ ...form, bedrooms: parseInt(e.target.value) || 0 })}
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/50">Baths</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={form.bathrooms}
                    onChange={(e) => setForm({ ...form, bathrooms: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/50">Sqft</Label>
                  <Input
                    type="number"
                    value={form.sqft}
                    onChange={(e) => setForm({ ...form, sqft: parseInt(e.target.value) || 0 })}
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Motivation & Timeline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/50">Motivation Level</Label>
              <Select value={form.motivation_level} onValueChange={(v) => setForm({ ...form, motivation_level: v })}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="very_motivated">Very Motivated</SelectItem>
                  <SelectItem value="motivated">Motivated</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="testing_market">Testing Market</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/50">Timeline</Label>
              <Select value={form.timeline} onValueChange={(v) => setForm({ ...form, timeline: v })}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asap">ASAP</SelectItem>
                  <SelectItem value="30_days">30 Days</SelectItem>
                  <SelectItem value="60_days">60 Days</SelectItem>
                  <SelectItem value="90_days">90 Days</SelectItem>
                  <SelectItem value="flexible">Flexible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-white/50">Notes</Label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full bg-black border border-white/[0.08] text-white p-3 mt-1"
              placeholder="Additional notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/[0.1] text-white/70 rounded-none">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-gold hover:bg-gold/90 text-black rounded-none">
              {saving ? "Saving..." : seller ? "Update Seller" : "Add Seller"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Marketplace Deal Card
function MarketplaceDealCard({ deal, onBid }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] hover:border-gold/30 transition-all duration-300">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-white font-medium">{deal.property_address}</h3>
            <p className="text-white/50 text-sm">
              {deal.property_type} • {deal.bedrooms}bd/{deal.bathrooms}ba • {deal.sqft?.toLocaleString()} sqft
            </p>
          </div>
          <span className={`px-2 py-1 text-xs font-medium border ${
            deal.deal_type === "flip" ? "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30" :
            deal.deal_type === "buy_hold" ? "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30" :
            "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500/30"
          }`}>
            {deal.deal_type?.replace(/_/g, " ").toUpperCase()}
          </span>
        </div>

        {/* Pricing */}
        <div className="bg-black border border-white/[0.08] p-3 mb-3">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-white/40 text-xs">Target Price</p>
              <p className="text-gold font-mono font-bold">{formatCurrency(deal.target_price)}</p>
            </div>
            <div>
              <p className="text-white/40 text-xs">ARV</p>
              <p className="text-white font-mono">{formatCurrency(deal.arv)}</p>
            </div>
            <div>
              <p className="text-white/40 text-xs">Max Commission</p>
              <p className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-bold">{deal.max_commission}%</p>
            </div>
          </div>
        </div>

        {/* Investor & Market */}
        <div className="flex items-center gap-4 text-sm mb-3">
          <span className="text-white/50">
            <User className="w-3 h-3 inline mr-1" /> {deal.investor_name || "Anonymous Investor"}
          </span>
          <span className="text-white/50">
            <MapPin className="w-3 h-3 inline mr-1" /> {deal.market}
          </span>
        </div>

        {/* Description */}
        {deal.description && (
          <p className="text-white/50 text-sm mb-3 line-clamp-2">{deal.description}</p>
        )}

        {/* Bid Status & Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-white/[0.08]">
          <div className="flex items-center gap-2">
            <span className="text-white/40 text-xs">{deal.bid_count || 0} bids</span>
            <span className="text-white/40 text-xs">•</span>
            <span className="text-white/40 text-xs">{deal.preferred_timeline_days} days timeline</span>
          </div>
          
          {deal.my_bid_status === "pending" ? (
            <span className="text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] text-sm flex items-center gap-1">
              <Clock className="w-4 h-4" /> Bid Pending
            </span>
          ) : deal.my_bid_status === "accepted" ? (
            <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] text-sm flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Won!
            </span>
          ) : deal.my_bid_status === "rejected" ? (
            <span className="text-[#FF3B30] text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> Not Selected
            </span>
          ) : (
            <Button
              size="sm"
              onClick={() => onBid(deal)}
              className="bg-gold hover:bg-gold/90 text-black rounded-none"
            >
              <Gavel className="w-4 h-4 mr-1" /> Place Bid
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Bid Modal
function BidModal({ isOpen, onClose, deal, realtor, onSubmit }) {
  const [form, setForm] = useState({
    commission_rate: 2.5,
    proposed_timeline_days: 90,
    message: "",
    experience_summary: "",
    specialties: []
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (deal) {
      setForm({
        commission_rate: deal.max_commission || 2.5,
        proposed_timeline_days: deal.preferred_timeline_days || 90,
        message: "",
        experience_summary: "",
        specialties: []
      });
    }
  }, [deal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(deal.deal_id, form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit bid");
    } finally {
      setSubmitting(false);
    }
  };

  const estimatedCommission = (deal?.target_price || 0) * (form.commission_rate / 100);

  if (!isOpen || !deal) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-md">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
            <Gavel className="w-5 h-5 text-gold" /> Submit Bid
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Deal Summary */}
          <div className="bg-black border border-white/[0.08] p-3">
            <p className="text-white font-medium">{deal.property_address}</p>
            <p className="text-gold font-mono">{formatCurrency(deal.target_price)}</p>
          </div>

          {/* Commission */}
          <div>
            <Label className="text-white/50">Your Commission Rate (%)</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max={deal.max_commission}
              value={form.commission_rate}
              onChange={(e) => setForm({ ...form, commission_rate: parseFloat(e.target.value) || 0 })}
              className="bg-black border-white/[0.08] text-white mt-1"
            />
            <p className="text-white/40 text-xs mt-1">
              Max allowed: {deal.max_commission}% • Est. commission: {formatCurrency(estimatedCommission)}
            </p>
          </div>

          {/* Timeline */}
          <div>
            <Label className="text-white/50">Proposed Timeline (days)</Label>
            <Input
              type="number"
              value={form.proposed_timeline_days}
              onChange={(e) => setForm({ ...form, proposed_timeline_days: parseInt(e.target.value) || 90 })}
              className="bg-black border-white/[0.08] text-white mt-1"
            />
          </div>

          {/* Message */}
          <div>
            <Label className="text-white/50">Message to Investor</Label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              className="w-full bg-black border border-white/[0.08] text-white p-3 mt-1"
              placeholder="Why should they choose you? Highlight your experience in this market..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/[0.1] text-white/70 rounded-none">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-gold hover:bg-gold/90 text-black rounded-none">
              <Send className="w-4 h-4 mr-2" /> {submitting ? "Submitting..." : "Submit Bid"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Sellers Page
export default function RealtorSellersPage() {
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sellers"); // sellers, marketplace, bids
  
  // Sellers state
  const [sellers, setSellers] = useState([]);
  const [showSellerModal, setShowSellerModal] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [sellerFilter, setSellerFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Marketplace state
  const [marketplaceDeals, setMarketplaceDeals] = useState([]);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  
  // Bids state
  const [myBids, setMyBids] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, sellersRes, marketplaceRes, bidsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/sellers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/marketplace/deals`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/realtor/marketplace/my-bids`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setRealtor(profileRes.data);
      setSellers(sellersRes.data);
      setMarketplaceDeals(marketplaceRes.data);
      setMyBids(bidsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/realtor");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/realtor/auth/logout`, {}, { withCredentials: true });
      navigate("/realtor");
    } catch (error) {
      navigate("/realtor");
    }
  };

  const handleSaveSeller = async (form, sellerId) => {
    try {
      if (sellerId) {
        await axios.put(`${BACKEND_URL}/api/realtor/sellers/${sellerId}`, form, { withCredentials: true });
        toast.success("Seller updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/realtor/sellers`, form, { withCredentials: true });
        toast.success("Seller added");
      }
      fetchData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteSeller = async (sellerId) => {
    if (!confirm("Are you sure you want to remove this seller?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/realtor/sellers/${sellerId}`, { withCredentials: true });
      toast.success("Seller removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove seller");
    }
  };

  const handleConvertToListing = async (sellerId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/realtor/sellers/${sellerId}/convert-to-listing`,
        {},
        { withCredentials: true }
      );
      toast.success("Seller converted to listing!");
      fetchData();
      navigate(`/realtor/listings/${res.data.listing_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert");
    }
  };

  const handleSubmitBid = async (dealId, form) => {
    await axios.post(
      `${BACKEND_URL}/api/realtor/marketplace/deals/${dealId}/bid`,
      form,
      { withCredentials: true }
    );
    toast.success("Bid submitted!");
    fetchData();
  };

  const handleWithdrawBid = async (bidId) => {
    if (!confirm("Withdraw this bid?")) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/realtor/marketplace/bids/${bidId}/withdraw`,
        {},
        { withCredentials: true }
      );
      toast.success("Bid withdrawn");
      fetchData();
    } catch (error) {
      toast.error("Failed to withdraw bid");
    }
  };

  // Filter sellers
  const filteredSellers = sellers.filter((s) => {
    const matchesSearch = !searchTerm ||
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.property_address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = sellerFilter === "all" || 
      (sellerFilter === "manual" && s.source === "manual") ||
      (sellerFilter === "marketplace" && s.source === "marketplace") ||
      s.status === sellerFilter;
    return matchesSearch && matchesFilter;
  });

  // Stats
  const manualSellers = sellers.filter(s => s.source === "manual").length;
  const marketplaceSellers = sellers.filter(s => s.source === "marketplace").length;
  const activeSellers = sellers.filter(s => s.status === "active").length;
  const pendingBids = myBids.filter(b => b.status === "pending").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="realtor-sellers-page">
      <RealtorSidebar realtor={realtor} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Sellers</h1>
            <p className="text-white/50 mt-1">Manage your seller pipeline and find new deals</p>
          </div>
          <Button
            onClick={() => {
              setEditingSeller(null);
              setShowSellerModal(true);
            }}
            className="gold-gradient-btn rounded-none font-bold"
            data-testid="add-seller-btn"
          >
            <Plus className="w-5 h-5 mr-2" /> Add Seller
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-5 h-5 text-white/50" />
              <span className="text-white/50 text-sm">Manual Sellers</span>
            </div>
            <p className="text-2xl font-bold text-white">{manualSellers}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-gold/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-gold" />
              <span className="text-white/50 text-sm">From Marketplace</span>
            </div>
            <p className="text-2xl font-bold text-gold">{marketplaceSellers}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
              <span className="text-white/50 text-sm">Active Sellers</span>
            </div>
            <p className="text-2xl font-bold text-white">{activeSellers}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Gavel className="w-5 h-5 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" />
              <span className="text-white/50 text-sm">Pending Bids</span>
            </div>
            <p className="text-2xl font-bold text-white">{pendingBids}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/[0.08] mb-6">
          <button
            onClick={() => setActiveTab("sellers")}
            className={`px-6 py-3 font-medium transition-all duration-300 ${
              activeTab === "sellers" 
                ? "text-gold border-b-2 border-gold" 
                : "text-white/50 hover:text-white"
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" /> My Sellers ({sellers.length})
          </button>
          <button
            onClick={() => setActiveTab("marketplace")}
            className={`px-6 py-3 font-medium transition-all duration-300 ${
              activeTab === "marketplace" 
                ? "text-gold border-b-2 border-gold" 
                : "text-white/50 hover:text-white"
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" /> Deal Marketplace ({marketplaceDeals.length})
          </button>
          <button
            onClick={() => setActiveTab("bids")}
            className={`px-6 py-3 font-medium transition-all duration-300 ${
              activeTab === "bids" 
                ? "text-gold border-b-2 border-gold" 
                : "text-white/50 hover:text-white"
            }`}
          >
            <Gavel className="w-4 h-4 inline mr-2" /> My Bids ({myBids.length})
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "sellers" && (
          <>
            {/* Filters */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6 flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                  <Input
                    placeholder="Search by name or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-black border-white/[0.08] text-white pl-10"
                  />
                </div>
              </div>
              <Select value={sellerFilter} onValueChange={setSellerFilter}>
                <SelectTrigger className="w-48 bg-black border-white/[0.08] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sellers</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                  <SelectItem value="marketplace">Marketplace Only</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="listed">Listed</SelectItem>
                  <SelectItem value="under_contract">Under Contract</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sellers Grid */}
            {filteredSellers.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSellers.map((seller) => (
                  <SellerCard
                    key={seller.seller_id}
                    seller={seller}
                    onEdit={(s) => { setEditingSeller(s); setShowSellerModal(true); }}
                    onDelete={handleDeleteSeller}
                    onConvert={handleConvertToListing}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <Building2 className="w-12 h-12 text-white/30 mx-auto mb-3" />
                <p className="text-white/50">No sellers found</p>
                <p className="text-white/40 text-sm mt-2">
                  Add manual sellers or win deals from the marketplace
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === "marketplace" && (
          <>
            {marketplaceDeals.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {marketplaceDeals.map((deal) => (
                  <MarketplaceDealCard
                    key={deal.deal_id}
                    deal={deal}
                    onBid={(d) => { setSelectedDeal(d); setShowBidModal(true); }}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <Zap className="w-12 h-12 text-white/30 mx-auto mb-3" />
                <p className="text-white/50">No deals available in the marketplace</p>
                <p className="text-white/40 text-sm mt-2">
                  Check back later - investors post deals seeking realtors regularly
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === "bids" && (
          <>
            {myBids.length > 0 ? (
              <div className="space-y-4">
                {myBids.map((bid) => (
                  <div key={bid.bid_id} className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-white font-medium">{bid.deal?.property_address || "Unknown Property"}</h3>
                        <p className="text-white/50 text-sm">
                          {bid.commission_rate}% commission • {bid.proposed_timeline_days} days timeline
                        </p>
                        <p className="text-gold font-mono mt-1">{formatCurrency(bid.estimated_commission)} est. commission</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-medium border ${
                          bid.status === "pending" ? "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30" :
                          bid.status === "accepted" ? "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30" :
                          bid.status === "rejected" ? "bg-red-500/20 text-[#FF3B30] border-red-500/30" :
                          "bg-white/20/20 text-white/50 border-white/[0.15]/30"
                        }`}>
                          {bid.status?.toUpperCase()}
                        </span>
                        {bid.status === "pending" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleWithdrawBid(bid.bid_id)}
                            className="text-white/50 hover:text-[#FF3B30] mt-2"
                          >
                            Withdraw
                          </Button>
                        )}
                      </div>
                    </div>
                    {bid.message && (
                      <p className="text-white/40 text-sm mt-2 pt-2 border-t border-white/[0.08]">{bid.message}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <Gavel className="w-12 h-12 text-white/30 mx-auto mb-3" />
                <p className="text-white/50">No bids submitted yet</p>
                <p className="text-white/40 text-sm mt-2">
                  Browse the marketplace and submit bids on deals
                </p>
                <Button
                  onClick={() => setActiveTab("marketplace")}
                  className="mt-4 gold-gradient-btn rounded-none"
                >
                  Browse Marketplace
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Modals */}
      <SellerFormModal
        isOpen={showSellerModal}
        onClose={() => { setShowSellerModal(false); setEditingSeller(null); }}
        seller={editingSeller}
        onSave={handleSaveSeller}
      />

      <BidModal
        isOpen={showBidModal}
        onClose={() => { setShowBidModal(false); setSelectedDeal(null); }}
        deal={selectedDeal}
        realtor={realtor}
        onSubmit={handleSubmitBid}
      />
    </div>
  );
}
